import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import { logout as logoutAction, setCredentials, tokenRefreshed, authBootstrapped, updateUser } from '../../features/auth/authSlice';
import { authApi } from '../../store/api/authApi';
import { baseApi } from '../../store/api/baseApi';
import { ForcePasswordChange } from '../../features/auth/ForcePasswordChange';
import { Spinner } from '../routes.shared';

// Evita que dos instancias de AuthGuard (shell + wallboard) disparen el refresh
// en paralelo. El flag del slice `bootstrapped` es la fuente de verdad; este ref
// solo corta la carrera dentro del mismo tick de montaje.
let bootstrapInFlight = false;

// La sesión persistida (redux-persist) guarda el user con los permisos calculados AL
// LOGUEARSE: si después cambian los permisos del rol, el menú queda viejo hasta un
// re-login. Refrescamos el user desde /auth/me UNA vez por carga de la app.
let userRefreshed = false;

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const bootstrapped = useAppSelector((s) => s.auth.bootstrapped);
  const userRole = useAppSelector((s) => s.auth.user?.role);
  const mustChangePassword = useAppSelector((s) => s.auth.user?.mustChangePassword);
  const location = useLocation();

  // Restauración de sesión al arrancar en frío (deep-link en pestaña nueva / link de
  // Gustavo): si todavía no resolvimos el bootstrap y no hay sesión en memoria,
  // intentamos un refresh silencioso con la cookie httpOnly antes de tirar a /login.
  // Sesión restaurada de localStorage → traer el user fresco (rol/permisos pueden
  // haber cambiado desde el login). Merge en background, sin bloquear el render.
  useEffect(() => {
    if (!isAuthenticated || userRefreshed) return;
    userRefreshed = true;
    dispatch(authApi.endpoints.getMe.initiate(undefined, { forceRefetch: true }))
      .unwrap()
      .then((user) => dispatch(updateUser(user)))
      .catch(() => { /* token vencido u offline: el flujo normal de reauth lo maneja */ });
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (bootstrapped || isAuthenticated) {
      if (!bootstrapped) dispatch(authBootstrapped());
      return;
    }
    if (bootstrapInFlight) return;
    bootstrapInFlight = true;
    let cancelled = false;
    (async () => {
      try {
        const { accessToken } = await dispatch(authApi.endpoints.refresh.initiate()).unwrap();
        dispatch(tokenRefreshed(accessToken));
        const user = await dispatch(authApi.endpoints.getMe.initiate(undefined, { forceRefetch: true })).unwrap();
        if (!cancelled) dispatch(setCredentials({ user, accessToken }));
      } catch {
        // No hay cookie válida → queda sin sesión y AuthGuard manda a /login.
      } finally {
        if (!cancelled) dispatch(authBootstrapped());
        bootstrapInFlight = false;
      }
    })();
    return () => { cancelled = true; };
  }, [bootstrapped, isAuthenticated, dispatch]);

  // Los roles de portal no deben estar en el CRM. Si quedaron pegados (sesión vieja
  // o login accidental), limpiamos y redirigimos al portal correspondiente.
  const isPortalRole = userRole === 'property_owner' || userRole === 'auction_bidder';

  useEffect(() => {
    if (isPortalRole) {
      dispatch(logoutAction());
      dispatch(baseApi.util.resetApiState());
    }
  }, [isPortalRole, dispatch]);

  if (isPortalRole) {
    const redirectTo = userRole === 'property_owner' ? '/portal/login' : '/subastas/ingresar';
    return <Navigate to={redirectTo} replace />;
  }

  // Mientras no se resolvió el intento de restaurar sesión, mostramos spinner en
  // vez de redirigir: así un deep-link con cookie válida abre la ficha directo.
  if (!bootstrapped) {
    return <Spinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Primer ingreso con clave temporal: bloquear el CRM hasta que cambie la contraseña.
  if (mustChangePassword) {
    return <ForcePasswordChange />;
  }

  return <>{children}</>;
}
